import { getAuthToken, isTokenExpired, forceLogout } from '../helpers/auth-helpers';
import { API_URL } from '../config';

/**
 * Centralized fetch utility that automatically handles authentication and token expiration
 * @param {string} endpoint - The API endpoint (without base URL)
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise} The fetch promise
 */
export const authenticatedFetch = async (endpoint, options = {}) => {
  const token = getAuthToken();
  
  // Check if token exists and is not expired
  if (!token) {
    console.error('No authentication token found');
    forceLogout();
    throw new Error('Authentication required');
  }
  
  if (isTokenExpired(token)) {
    console.error('Authentication token has expired');
    forceLogout();
    throw new Error('Token expired');
  }
  
  // Prepare headers
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  
  // Make the request
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    // Handle authentication errors
    if (response.status === 401) {
      console.error('Authentication failed - token may be expired');
      forceLogout();
      throw new Error('Authentication failed');
    }
    
    if (response.status === 403) {
      console.error('Access forbidden');
      forceLogout();
      throw new Error('Access forbidden');
    }
    
    return response;
  } catch (error) {
    // Handle network errors
    if (error.message.includes('Authentication') || error.message.includes('Token')) {
      throw error; // Re-throw auth errors
    }
    
    console.error('Network error:', error);
    throw new Error('Network error occurred');
  }
};

/**
 * Convenient wrapper for GET requests
 * @param {string} endpoint - The API endpoint
 * @param {object} params - Query parameters
 * @returns {Promise} The response data
 */
export const authenticatedGet = async (endpoint, params = {}) => {
  const url = new URL(endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`);
  
  // Add query parameters
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      url.searchParams.append(key, params[key]);
    }
  });
  
  const response = await authenticatedFetch(url.toString());
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Convenient wrapper for POST requests
 * @param {string} endpoint - The API endpoint
 * @param {object} data - Request body data
 * @returns {Promise} The response data
 */
export const authenticatedPost = async (endpoint, data = {}) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Convenient wrapper for PUT requests
 * @param {string} endpoint - The API endpoint
 * @param {object} data - Request body data
 * @returns {Promise} The response data
 */
export const authenticatedPut = async (endpoint, data = {}) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Convenient wrapper for DELETE requests
 * @param {string} endpoint - The API endpoint
 * @returns {Promise} The response data
 */
export const authenticatedDelete = async (endpoint) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export default authenticatedFetch;
